import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { PaymentEventsMetricsApi } from '../../services/ApiService';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import './Settings.css';

const COLORS = ['#007bff', '#28a745', '#ffc107', '#dc3545', '#17a2b8', '#6f42c1', '#20c997', '#fd7e14'];

function StatCard({ label, value, subtitle }) {
  return (
    <div style={{ background: 'var(--card-bg)', padding: '1.25rem', borderRadius: '10px', boxShadow: 'var(--shadow-sm)', border: '1px solid var(--border-light)' }}>
      <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: '0.5rem', fontWeight: 500 }}>{label}</div>
      <div style={{ fontSize: '1.75rem', fontWeight: 'bold', color: '#007bff' }}>{value}</div>
      {subtitle && <div style={{ fontSize: '0.8rem', color: '#888', marginTop: '0.25rem' }}>{subtitle}</div>}
    </div>
  );
}

export default function WebhookMonitor() {
  const { user } = useAuth();
  const isAdmin = user?.roles?.some(role => role.roleName === 'admin');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);
  const [successRate, setSuccessRate] = useState(null);
  const [volumeSeries, setVolumeSeries] = useState([]);
  const [eventTypeDistribution, setEventTypeDistribution] = useState([]);
  const [providerDistribution, setProviderDistribution] = useState([]);
  const [latency, setLatency] = useState(null);
  const [filters, setFilters] = useState({
    from: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
    to: new Date().toISOString(),
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { from, to } = filters;
      const fromDate = from ? new Date(from) : null;
      const toDate = to ? new Date(to) : null;

      const [summaryRes, successRateRes, volumeRes, eventTypeRes, providerRes, latencyRes] = await Promise.all([
        PaymentEventsMetricsApi.getSummary({ from: fromDate, to: toDate }),
        PaymentEventsMetricsApi.getSuccessRate({ from: fromDate, to: toDate }),
        PaymentEventsMetricsApi.getVolumeSeries({ from: fromDate, to: toDate }),
        PaymentEventsMetricsApi.getEventTypeDistribution({ from: fromDate, to: toDate }),
        PaymentEventsMetricsApi.getProviderDistribution({ from: fromDate, to: toDate }),
        PaymentEventsMetricsApi.getProcessingLatency({ from: fromDate, to: toDate }),
      ]);

      if (summaryRes.ok) {
        setSummary(await summaryRes.json());
      }
      if (successRateRes.ok) {
        setSuccessRate(await successRateRes.json());
      }
      if (volumeRes.ok) {
        const data = await volumeRes.json();
        setVolumeSeries(
          data.map((d) => ({
            date: new Date(d.date).toLocaleDateString(),
            total: Number(d.count || 0),
            processed: Number(d.processed_count || 0),
          }))
        );
      }
      if (eventTypeRes.ok) {
        const data = await eventTypeRes.json();
        setEventTypeDistribution(
          data.map((d) => ({
            name: d.eventType || d.eventtype,
            value: Number(d.count),
          }))
        );
      }
      if (providerRes.ok) {
        const data = await providerRes.json();
        setProviderDistribution(
          data.map((d) => ({
            name: d.provider,
            value: Number(d.count),
          }))
        );
      }
      if (latencyRes.ok) {
        setLatency(await latencyRes.json());
      }
    } catch (err) {
      console.error('Error fetching webhook metrics:', err);
      setError(err.message || 'Failed to load webhook metrics');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    if (isAdmin) {
      fetchData();
    }
  }, [isAdmin, filters, fetchData]);

  const handleDatePreset = (days) => {
    setFilters({
      from: new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString(),
      to: new Date().toISOString(),
    });
  };

  if (!isAdmin) {
    return (
      <div className="settings-container">
        <h2>Webhook Monitor</h2>
        <div>Admin access required.</div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="settings-container">
        <h2>Webhook Monitor</h2>
        <div>Loading webhook metrics...</div>
      </div>
    );
  }

  return (
    <div className="settings-container">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.75rem' }}>
        <h2 style={{ margin: 0 }}>Webhook Monitor</h2>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button onClick={() => fetchData()} disabled={loading}>
            🔄 Refresh
          </button>
          <button onClick={() => handleDatePreset(1)}>24h</button>
          <button onClick={() => handleDatePreset(7)}>7d</button>
          <button onClick={() => handleDatePreset(30)}>30d</button>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div style={{ marginBottom: '1.5rem', padding: '1rem', background: '#ffebee', border: '1px solid #f44336', borderRadius: '8px', color: '#b71c1c' }}>
          Error loading webhook metrics: {error}
          <button onClick={fetchData} style={{ marginLeft: '1rem', padding: '0.25rem 0.75rem', cursor: 'pointer' }}>Retry</button>
        </div>
      )}

      {/* Summary Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
        <StatCard label="Total Events" value={(summary?.totalEvents || 0).toLocaleString()} />
        <StatCard label="Processed" value={(successRate?.processed || 0).toLocaleString()} subtitle={`${successRate?.successRate || 0}% success rate`} />
        <StatCard label="Unprocessed" value={(successRate?.unprocessed || 0).toLocaleString()} />
        <StatCard label="Event Types" value={summary?.distinctEventTypes || 0} />
      </div>

      {/* Processing Latency */}
      {latency && latency.totalProcessed > 0 && (
        <div style={{ background: 'var(--card-bg)', padding: '1.5rem', borderRadius: '12px', boxShadow: 'var(--shadow-sm)', border: '1px solid var(--border-light)', marginBottom: '1.5rem' }}>
          <h3 style={{ marginTop: 0 }}>Processing Latency</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1rem' }}>
            <div>
              <div style={{ fontSize: '0.85rem', color: '#666' }}>P50 (Median)</div>
              <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>{latency.p50Ms || 0}ms</div>
            </div>
            <div>
              <div style={{ fontSize: '0.85rem', color: '#666' }}>P95</div>
              <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>{latency.p95Ms || 0}ms</div>
            </div>
            <div>
              <div style={{ fontSize: '0.85rem', color: '#666' }}>P99</div>
              <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>{latency.p99Ms || 0}ms</div>
            </div>
            <div>
              <div style={{ fontSize: '0.85rem', color: '#666' }}>Average</div>
              <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>{latency.avgMs || 0}ms</div>
            </div>
          </div>
        </div>
      )}

      {/* Volume Time Series */}
      {volumeSeries.length > 0 ? (
        <div style={{ background: 'var(--card-bg)', padding: '1.5rem', borderRadius: '12px', boxShadow: 'var(--shadow-sm)', border: '1px solid var(--border-light)', marginBottom: '1.5rem' }}>
          <h3 style={{ marginTop: 0 }}>Daily Volume</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={volumeSeries}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="total" stroke="#007bff" name="Total Events" strokeWidth={2} />
              <Line type="monotone" dataKey="processed" stroke="#28a745" name="Processed" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div style={{ background: 'var(--card-bg)', padding: '1.5rem', borderRadius: '12px', boxShadow: 'var(--shadow-sm)', border: '1px solid var(--border-light)', marginBottom: '1.5rem', textAlign: 'center', color: '#6b7280' }}>
          <h3 style={{ marginTop: 0 }}>Daily Volume</h3>
          <p>No volume data available for this period.</p>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '1.5rem', marginBottom: '1.5rem' }}>
        {/* Event Type Distribution */}
        {eventTypeDistribution.length > 0 && (
          <div style={{ background: 'var(--card-bg)', padding: '1.5rem', borderRadius: '12px', boxShadow: 'var(--shadow-sm)', border: '1px solid var(--border-light)' }}>
            <h3 style={{ marginTop: 0 }}>Event Type Distribution</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={eventTypeDistribution}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
                <YAxis />
                <Tooltip />
                <Bar dataKey="value" fill="#007bff" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Provider Distribution */}
        {providerDistribution.length > 0 && (
          <div style={{ background: 'var(--card-bg)', padding: '1.5rem', borderRadius: '12px', boxShadow: 'var(--shadow-sm)', border: '1px solid var(--border-light)' }}>
            <h3 style={{ marginTop: 0 }}>Provider Distribution</h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={providerDistribution}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {providerDistribution.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Event Types Table */}
      {eventTypeDistribution.length > 0 && (
        <div style={{ background: 'var(--card-bg)', padding: '1.5rem', borderRadius: '12px', boxShadow: 'var(--shadow-sm)', border: '1px solid var(--border-light)' }}>
          <h3 style={{ marginTop: 0 }}>Event Types Breakdown</h3>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--border-light)' }}>
                <th style={{ textAlign: 'left', padding: '0.75rem' }}>Event Type</th>
                <th style={{ textAlign: 'right', padding: '0.75rem' }}>Count</th>
                <th style={{ textAlign: 'right', padding: '0.75rem' }}>Percentage</th>
              </tr>
            </thead>
            <tbody>
              {(() => {
                const total = eventTypeDistribution.reduce((sum, i) => sum + i.value, 0);
                return eventTypeDistribution.map((item, idx) => {
                  const percentage = total > 0 ? (item.value / total * 100).toFixed(1) : 0;
                  return (
                    <tr key={idx} style={{ borderBottom: '1px solid var(--border-light)' }}>
                      <td style={{ padding: '0.75rem' }}>{item.name}</td>
                      <td style={{ textAlign: 'right', padding: '0.75rem' }}>{item.value.toLocaleString()}</td>
                      <td style={{ textAlign: 'right', padding: '0.75rem' }}>{percentage}%</td>
                    </tr>
                  );
                });
              })()}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
